/**
 * Shared link extension configuration for Tiptap.
 *
 * Both the editor (write path, `Editor.tsx`) and the renderer (read path,
 * `localized-text-editor/rich-text-editor/extentions.ts` → `RenderDocAsHTML`)
 * depend on this. The two MUST use the same protocol allowlist or the read
 * and write contracts diverge — at which point DOMPurify becomes the only
 * gate against unsafe schemes flowing into HTML.
 *
 * The protocol list also mirrors the ATS server-side validator at
 * `api/src/main/java/ca/avanti/ats/validation/RichTextPolicy.java`. See
 * `coder/patterns/avanti-plus.md` §"Rich-text wire format" for the full
 * cross-repo contract.
 */
import Link from "@tiptap/extension-link";

export const ALLOWED_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];
export const DEFAULT_LINK_PROTOCOL = "https://";

const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;

export function isAllowedLinkUrl(url: string): boolean {
  // Link-mark hrefs must be absolute (carry their own scheme). Bare paths or
  // scheme-less domains are rejected here; callers that want to accept a bare
  // domain (e.g. the link dialog's typed input) should normalize via
  // normalizeLinkHref in Editor.tsx before calling this.
  if (!SCHEME_PREFIX.test(url)) {
    return false;
  }

  try {
    const parsed = new URL(url);

    return ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol.toLowerCase());
  } catch {
    return false;
  }
}

export function normalizeLinkHref(href: string): string | undefined {
  const trimmed = href.trim();

  if (!trimmed) {
    return "";
  }

  const withProtocol = SCHEME_PREFIX.test(trimmed) ? trimmed : `${DEFAULT_LINK_PROTOCOL}${trimmed}`;

  try {
    const parsed = new URL(withProtocol);

    if (!ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol.toLowerCase())) {
      return undefined;
    }

    return parsed.href;
  } catch {
    return undefined;
  }
}

const BLOCKED_LINK_ATTRIBUTES = new Set(["class", "title"]);

const SafelistConformingLink = Link.extend({
  addAttributes() {
    const inherited = this.parent?.() ?? {};

    return Object.fromEntries(
      Object.entries(inherited).filter(([name]) => !BLOCKED_LINK_ATTRIBUTES.has(name))
    );
  },
});

export function configuredLinkExtension(options?: { autolink?: boolean }) {
  return SafelistConformingLink.configure({
    openOnClick: false,
    autolink: options?.autolink ?? false,
    defaultProtocol: "https",
    isAllowedUri: (url, { defaultValidate }) => {
      const allowed = isAllowedLinkUrl(url);

      return allowed ? defaultValidate(url) : false;
    },
    HTMLAttributes: {
      target: "_blank",
      rel: "noopener noreferrer",
    },
  });
}
